import { Schema } from "effect";
import { EgkAttestationHandle } from "../patient-info/egk-attestation.schema";

/**
 * Gender values accepted by the FrontDesk+ kiosk patient-creation operation.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const gender: KioskPatientGender = "MALE";
 * ```
 */
export const KioskPatientGender = Schema.Literals(
  ["MALE",
  "FEMALE",
  "VARIOUS",
  "UNKNOWN"],
);

/**
 * Decoded kiosk patient gender.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const gender: KioskPatientGender = "UNKNOWN";
 * ```
 */
export type KioskPatientGender = typeof KioskPatientGender.Type;

/**
 * Salutations accepted by the FrontDesk+ kiosk patient-creation operation.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const salutation: KioskPatientSalutation = "NONE";
 * ```
 */
export const KioskPatientSalutation = Schema.Literals(["MR", "MS", "NONE"]);

/**
 * Decoded kiosk patient salutation.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const salutation: KioskPatientSalutation = "MS";
 * ```
 */
export type KioskPatientSalutation = typeof KioskPatientSalutation.Type;

/**
 * Insurance categories accepted for kiosk patient creation.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const insurance: KioskPatientInsurance = "GKV";
 * ```
 */
export const KioskPatientInsurance = Schema.Literals(["GKV", "PKV", "SELFPAY"]);

/**
 * Decoded kiosk patient insurance category.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const insurance: KioskPatientInsurance = "SELFPAY";
 * ```
 */
export type KioskPatientInsurance = typeof KioskPatientInsurance.Type;

/**
 * Client-safe payload for one policy-governed kiosk patient creation.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const request = new KioskCreatePatientRequest({
 *   firstname: "Erika",
 *   lastname: "Beispiel",
 *   dob: "1990-01-15",
 *   gender: "FEMALE",
 *   talk: "MS",
 *   insurance: "GKV",
 * });
 * ```
 */
export class KioskCreatePatientRequest extends Schema.Class<KioskCreatePatientRequest>(
  "KioskCreatePatientRequest",
)({
  firstname: Schema.String,
  lastname: Schema.String,
  dob: Schema.String,
  gender: KioskPatientGender,
  talk: KioskPatientSalutation,
  insurance: KioskPatientInsurance,
  insurerId: Schema.optional(Schema.String),
  insurerName: Schema.optional(Schema.String),
  policyNumber: Schema.optional(Schema.String),
  egkAttestation: Schema.optional(EgkAttestationHandle),
}) {}

/**
 * Successful kiosk patient-creation response.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const response: KioskCreatePatientResponse = { patientId: "patient-1" };
 * ```
 */
export const KioskCreatePatientResponse = Schema.Struct({
  patientId: Schema.String,
});

/**
 * Decoded successful kiosk patient-creation response.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const response: KioskCreatePatientResponse = { patientId: "patient-1" };
 * ```
 */
export type KioskCreatePatientResponse = typeof KioskCreatePatientResponse.Type;

/**
 * Coarse, patient-safe reasons a kiosk patient creation did not complete.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const reason: KioskPatientCreationRejectionReason = "policy-disabled";
 * ```
 */
export const KioskPatientCreationRejectionReason = Schema.Literals(
  ["policy-disabled",
  "policy-unavailable",
  "creation-failed"],
);

/**
 * Decoded kiosk patient-creation rejection reason.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const reason: KioskPatientCreationRejectionReason = "creation-failed";
 * ```
 */
export type KioskPatientCreationRejectionReason =
  typeof KioskPatientCreationRejectionReason.Type;

/**
 * Typed, non-sensitive rejection returned by kiosk patient creation.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const error: KioskPatientCreationRejected = {
 *   _tag: "KioskPatientCreationRejected",
 *   reason: "policy-unavailable",
 * };
 * ```
 */
export const KioskPatientCreationRejected = Schema.TaggedStruct(
  "KioskPatientCreationRejected",
  {
    reason: KioskPatientCreationRejectionReason,
  },
);

/**
 * Decoded typed rejection returned by kiosk patient creation.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const error: KioskPatientCreationRejected = {
 *   _tag: "KioskPatientCreationRejected",
 *   reason: "policy-disabled",
 * };
 * ```
 */
export type KioskPatientCreationRejected =
  typeof KioskPatientCreationRejected.Type;
